import React, { useCallback, useMemo, useState } from 'react';

import { Button } from 'primereact/button';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { Tooltip } from 'primereact/tooltip';
import { useFormContext } from 'react-hook-form';
import { DropdownWithDeletedValueTemplate } from 'utils/DropdownWithDeletedValueTemplate';

import {
  DepartmentCalendar,
  ScheduleWithDates,
  TreeNode
} from '../types/interfaces';

import NewDatepicker from './custom-controls/NewDatepicker';
import DeleteConfirmationDialog from './shared/DeleteConfirmationDialog';

interface DepartmentCalendarComponentProps {
  passedClassName: string;
  index: number;
  item: DepartmentCalendar | ScheduleWithDates;
  options: TreeNode[];
  onDelete: () => void;
  onUpdate: (updated: any) => void;
  errors?: any;
  /* New optional props with defaults */
  idField: 'calendarId' | 'scheduleId';
  fieldPrefix: string;
  placeholder?: string;
  fieldTranslate?: {
    directTranslate: string;
    tooltip: string;
  };
  useDateOnlyFormat?: boolean;
  departmentsOptions?: TreeNode[]; // Новый пропс - список участков сотрудника
  showDepartmentField?: boolean; // Флаг для отображения поля участка
  departmentIdField?: string; // Название поля для departmentId (default: 'departmentId')
}

export const TimeViewComponent: React.FC<DepartmentCalendarComponentProps> = ({
  passedClassName,
  index,
  item,
  options,
  onDelete,
  onUpdate,
  errors,
  idField = 'calendarId', // Default value for backward compatibility
  fieldPrefix = 'departmentCalendars',
  placeholder,
  fieldTranslate = {
    directTranslate: 'календарь',
    tooltip: 'календаря'
  },
  useDateOnlyFormat,
  departmentsOptions = [], // По умолчанию пустой массив
  showDepartmentField = false, // По умолчанию скрыто
  departmentIdField = 'departmentId'
}) => {
  const { trigger } = useFormContext();
  const [confirmVisible, setConfirmVisible] = useState(false);
  const memoizedCalendars = useMemo(() => options, [options]);

  const selectedDepartment = useMemo(() => {
    if (!showDepartmentField || !departmentsOptions) return undefined;
    const deptId = (item as any)[departmentIdField]?.toString() ?? '';
    return (
      departmentsOptions.find((d) => d.key?.toString() === deptId) ||
      (deptId !== '0'
        ? { key: (item as any)[departmentIdField], label: '' }
        : undefined)
    );
  }, [departmentsOptions, item, showDepartmentField, departmentIdField]);

  // Обработчик выбора участка
  const handleDepartmentSelect = useCallback(
    (e: DropdownChangeEvent) => {
      const selectedNode = e.value;
      const update = {
        ...item,
        [departmentIdField]: Number(selectedNode?.key) || 0
      };
      onUpdate(update);
      trigger(`${fieldPrefix}.${index}.${departmentIdField}`);
    },
    [item, index, onUpdate, departmentIdField, fieldPrefix, trigger]
  );

  const selectedCalendar =
    memoizedCalendars.find(
      (c) => c.id?.toString() === ((item as any)[idField]?.toString() ?? '')
    ) ??
    ((item as any)[idField] !== 0
      ? {
          id: (item as any)[idField],
          label: 'name' in item ? item.name : ''
        }
      : undefined);

  const handleCalendarSelect = useCallback(
    (e: DropdownChangeEvent) => {
      const selectedNode = e.value;
      const update = {
        ...item,
        name: e.value?.label,
        [idField]: selectedNode?.id || 0
      };
      onUpdate(update);
      trigger(`${fieldPrefix}.${index}.${idField}`);
    },
    [item, index, onUpdate, idField, fieldPrefix, trigger]
  );

  const handleDateChange = useCallback(
    (dates: { dateFrom: string; dateUntil: string }) => {
      const update = {
        ...item,
        startDate: dates.dateFrom,
        endDate: dates.dateUntil
      };
      onUpdate(update);
      trigger([
        `${fieldPrefix}.${index}.startDate`,
        `${fieldPrefix}.${index}.endDate`
      ]);
    },
    [index, onUpdate, trigger, fieldPrefix, item]
  );

  const dropdownPlaceholder =
    item?.name === '' && (!item.id || item.id === 0)
      ? placeholder ?? 'Выберите календарь'
      : selectedCalendar?.label ??
        item?.name ??
        placeholder ??
        'Выберите календарь';

  return (
    <div
      className={
        passedClassName +
        ' item-item flex flex-row justify-content-between w-auto'
      }
    >
      <div className="flex p-align-center gap-1">
        <div
          className="p-col-3"
          style={{
            width: showDepartmentField && departmentsOptions ? '223px' : '250px'
          }}
        >
          <Dropdown
            value={selectedCalendar}
            options={memoizedCalendars}
            onChange={handleCalendarSelect}
            optionLabel="label"
            placeholder={dropdownPlaceholder}
            className={`w-full ${errors?.[idField] ? 'p-invalid' : ''}`}
            filter
            showClear
            tooltip={dropdownPlaceholder}
            valueTemplate={(option) => (
              <DropdownWithDeletedValueTemplate
                option={option}
                selectedItem={selectedCalendar ? { label: selectedCalendar.label || '' } : null}
                placeholder={dropdownPlaceholder}
                tooltipText="Удалено в справочнике"
              />
            )}
          />
          <Tooltip target=".deleted-status-tooltip" />
          <p
            className={`asset-type-card-errors card-errors ${
              errors?.[idField] ? 'text-red-500' : 'text-transparent'
            } `}
          >
            {errors?.[idField] ? errors?.[idField].message : ' '}
          </p>
        </div>

        {showDepartmentField && departmentsOptions && (
          <div className="p-col-3" style={{ width: '332px' }}>
            <Dropdown
              value={selectedDepartment}
              options={departmentsOptions}
              onChange={handleDepartmentSelect}
              optionLabel="label"
              placeholder="Выберите участок"
              className={`w-full ${
                errors?.[departmentIdField] ? 'p-invalid' : ''
              }`}
              filter
              showClear
              tooltip={selectedDepartment?.label ? selectedDepartment?.label : 'Выберите участок' }
              disabled={departmentsOptions.length === 0}
            />
            <p
              className={`asset-type-card-errors card-errors ${
                errors?.[departmentIdField]
                  ? 'text-red-500'
                  : 'text-transparent'
              } `}
            >
              {errors?.[departmentIdField]?.message || ' '}
            </p>
          </div>
        )}

        <div className="p-col">
          <NewDatepicker
            customWidth={
              showDepartmentField && departmentsOptions ? '147px' : null
            }
            onChange={handleDateChange}
            datepickerDate={{
              dateFrom: item.startDate,
              dateUntil: item.endDate
            }}
            errors={errors}
            tooltip={{
              left: `Выбор даты начала действия ${fieldTranslate.tooltip}`,
              right: `Выбор даты завершения действия ${fieldTranslate.tooltip}`
            }}
            gap="gap-1"
            useDateOnlyFormat={useDateOnlyFormat ?? false}
          />
        </div>

        <div>
          <Button
            icon="pi pi-trash"
            className="p-button-danger"
            onClick={() => setConfirmVisible(true)}
          />
        </div>
      </div>

      <DeleteConfirmationDialog
        visible={confirmVisible}
        onHide={() => setConfirmVisible(false)}
        onConfirm={onDelete}
      >
        Вы уверены, что хотите удалить {fieldTranslate.directTranslate}?
      </DeleteConfirmationDialog>
    </div>
  );
};
